import { create } from 'zustand';
import { api } from '../services/api';

export interface RecipeIngredient {
  id: string;
  recipe_id: string;
  name: string;
  quantity: string;
  unit: string;
  category: string;
  sort_order: number;
}

export interface RecipeStep {
  id: string;
  recipe_id: string;
  step_number: number;
  instruction: string;
}

export interface Recipe {
  id: string;
  family_id: string;
  title: string;
  description: string;
  servings: number;
  prep_time_mins: number;
  cook_time_mins: number;
  cuisine: string;
  tags: string[];
  image_url?: string;
  created_by: string;
  ingredients: RecipeIngredient[];
  steps: RecipeStep[];
  created_at: string;
}

export interface MealPlan {
  id: string;
  family_id: string;
  date: string;
  meal_type: string;
  recipe_id?: string;
  recipe_title?: string;
  recipe_image_url?: string;
  custom_meal?: string;
  notes?: string;
  cook_user_id?: string | null;
  cook_name?: string | null;
  created_by: string;
  created_at: string;
}

interface KitchenState {
  recipes: Recipe[];
  currentRecipe: Recipe | null;
  mealPlans: MealPlan[];
  isLoading: boolean;
  error: string | null;
  fetchRecipes: (filters?: { cuisine?: string; tag?: string; search?: string }) => Promise<void>;
  fetchRecipe: (id: string) => Promise<void>;
  createRecipe: (data: Partial<Recipe>) => Promise<Recipe>;
  updateRecipe: (id: string, data: Partial<Recipe>) => Promise<Recipe>;
  deleteRecipe: (id: string) => Promise<void>;
  fetchMealPlans: (week: string) => Promise<void>;
  createMealPlan: (data: Partial<MealPlan>) => Promise<MealPlan>;
  updateMealPlan: (id: string, data: Partial<MealPlan>) => Promise<MealPlan>;
  deleteMealPlan: (id: string) => Promise<void>;
  deleteMealPlans: (ids: string[]) => Promise<void>;
  copyWeek: (sourceWeekStart: string, targetWeekStart: string) => Promise<{ created: MealPlan[]; skipped: number }>;
  fillEmptySlots: (weekStart: string) => Promise<MealPlan[]>;
  planWeekWithAi: (weekStart: string, theme?: string) => Promise<MealPlan[]>;
  handleWsMessage: (message: any) => void;
}

export const useKitchenStore = create<KitchenState>((set, get) => ({
  recipes: [],
  currentRecipe: null,
  mealPlans: [],
  isLoading: false,
  error: null,

  fetchRecipes: async (filters) => {
    set({ isLoading: true, error: null });
    try {
      const params = new URLSearchParams();
      if (filters?.cuisine) params.append('cuisine', filters.cuisine);
      if (filters?.tag) params.append('tag', filters.tag);
      if (filters?.search) params.append('search', filters.search);
      const query = params.toString();
      const res = await api.get(`/kitchen/recipes${query ? `?${query}` : ''}`);
      set({ recipes: res.data.recipes, isLoading: false });
    } catch (err: any) {
      set({ error: err.message, isLoading: false });
    }
  },

  fetchRecipe: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const res = await api.get(`/kitchen/recipes/${id}`);
      set({ currentRecipe: res.data.recipe, isLoading: false });
    } catch (err: any) {
      set({ error: err.message, isLoading: false });
    }
  },

  createRecipe: async (data) => {
    try {
      const res = await api.post('/kitchen/recipes', data);
      const newRecipe = res.data.recipe;
      set(state => ({ recipes: [newRecipe, ...state.recipes] }));
      return newRecipe;
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  updateRecipe: async (id, data) => {
    try {
      const res = await api.put(`/kitchen/recipes/${id}`, data);
      const updatedRecipe = res.data.recipe;
      set(state => ({
        recipes: state.recipes.map(r => r.id === id ? updatedRecipe : r),
        currentRecipe: state.currentRecipe?.id === id ? updatedRecipe : state.currentRecipe,
      }));
      return updatedRecipe;
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  deleteRecipe: async (id) => {
    try {
      await api.delete(`/kitchen/recipes/${id}`);
      set(state => ({
        recipes: state.recipes.filter(r => r.id !== id),
        currentRecipe: state.currentRecipe?.id === id ? null : state.currentRecipe,
      }));
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  fetchMealPlans: async (week) => {
    set({ isLoading: true, error: null });
    try {
      const res = await api.get(`/kitchen/meal-plans?week=${week}`);
      const plans = (res.data.meal_plans || res.data.mealPlans || []).map((p: any) => ({
        ...p,
        date: p.date?.length > 10 ? p.date.slice(0, 10) : p.date,
      }));
      set({ mealPlans: plans, isLoading: false });
    } catch (err: any) {
      set({ error: err.message, isLoading: false });
    }
  },

  createMealPlan: async (data) => {
    try {
      const res = await api.post('/kitchen/meal-plans', data);
      const newPlan = res.data.meal_plan || res.data.mealPlan;
      if (!newPlan) throw new Error('Failed to create meal plan');
      // Normalize date to yyyy-MM-dd to match WeekPlan keys
      if (newPlan.date && newPlan.date.length > 10) {
        newPlan.date = newPlan.date.slice(0, 10);
      }
      set(state => {
        const exists = state.mealPlans.find(p => p.id === newPlan.id);
        if (exists) return state;
        return { mealPlans: [...state.mealPlans, newPlan] };
      });
      return newPlan;
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  updateMealPlan: async (id, data) => {
    try {
      const res = await api.put(`/kitchen/meal-plans/${id}`, data);
      const updatedPlan = res.data.meal_plan || res.data.mealPlan;
      set(state => ({
        mealPlans: state.mealPlans.map(p => p.id === id ? updatedPlan : p),
      }));
      return updatedPlan;
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  deleteMealPlan: async (id) => {
    try {
      await api.delete(`/kitchen/meal-plans/${id}`);
      set(state => ({
        mealPlans: state.mealPlans.filter(p => p.id !== id),
      }));
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  deleteMealPlans: async (ids) => {
    if (!ids || ids.length === 0) return;
    // The backend doesn't have a bulk delete endpoint and adding one for
    // the sake of undo feels like over-engineering — fire the deletes in
    // parallel and swallow individual failures so a single flaky row
    // can't leave the ledger in a half-undone state.
    await Promise.all(ids.map(id => api.delete(`/kitchen/meal-plans/${id}`).catch(() => null)));
    set(state => ({
      mealPlans: state.mealPlans.filter(p => !ids.includes(p.id)),
    }));
  },

  copyWeek: async (sourceWeekStart, targetWeekStart) => {
    try {
      const res = await api.post('/kitchen/meal-plans/copy-week', {
        source_week_start: sourceWeekStart,
        target_week_start: targetWeekStart,
      }, { timeout: 30000 });
      const created = (res.data.created || []).map((p: any) => ({
        ...p,
        date: p.date?.length > 10 ? p.date.slice(0, 10) : p.date,
      }));
      set(state => ({ mealPlans: [...state.mealPlans, ...created] }));
      return { created, skipped: res.data.skipped || 0 };
    } catch (err: any) {
      set({ error: err.response?.data?.error || err.message });
      throw err;
    }
  },

  fillEmptySlots: async (weekStart) => {
    try {
      const res = await api.post('/kitchen/meal-plans/fill-empty', { week_start: weekStart }, { timeout: 60000 });
      const created = (res.data.created || []).map((p: any) => ({
        ...p,
        date: p.date?.length > 10 ? p.date.slice(0, 10) : p.date,
      }));
      set(state => ({ mealPlans: [...state.mealPlans, ...created] }));
      return created;
    } catch (err: any) {
      set({ error: err.response?.data?.error || err.message });
      throw err;
    }
  },

  planWeekWithAi: async (weekStart, theme) => {
    try {
      const res = await api.post(
        '/kitchen/meal-plans/plan-week',
        { week_start: weekStart, theme: theme || undefined },
        { timeout: 120000 }
      );
      const created = (res.data.created || []).map((p: any) => ({
        ...p,
        date: p.date?.length > 10 ? p.date.slice(0, 10) : p.date,
      }));
      set(state => ({ mealPlans: [...state.mealPlans, ...created] }));
      return created;
    } catch (err: any) {
      set({ error: err.response?.data?.error || err.message });
      throw err;
    }
  },

  handleWsMessage: (message) => {
    const { currentRecipe } = get();

    switch (message.type) {
      case 'RECIPE_CREATED':
        set(state => {
          const exists = state.recipes.find(r => r.id === message.payload.id);
          if (exists) return state;
          return { recipes: [message.payload, ...state.recipes] };
        });
        break;

      case 'RECIPE_UPDATED':
        set(state => ({
          recipes: state.recipes.map(r => r.id === message.payload.id ? { ...r, ...message.payload } : r),
          currentRecipe: currentRecipe?.id === message.payload.id ? { ...currentRecipe, ...message.payload } : currentRecipe,
        }));
        break;

      case 'RECIPE_DELETED':
        set(state => ({
          recipes: state.recipes.filter(r => r.id !== (message.payload.recipeId || message.payload.id)),
          currentRecipe: currentRecipe?.id === (message.payload.recipeId || message.payload.id) ? null : currentRecipe,
        }));
        break;

      case 'MEAL_PLAN_CREATED':
        set(state => {
          const exists = state.mealPlans.find(p => p.id === message.payload.id);
          if (exists) return state;
          return { mealPlans: [...state.mealPlans, message.payload] };
        });
        break;

      case 'MEAL_PLAN_UPDATED':
        set(state => ({
          mealPlans: state.mealPlans.map(p => p.id === message.payload.id ? { ...p, ...message.payload } : p),
        }));
        break;

      case 'MEAL_PLAN_DELETED':
        set(state => ({
          mealPlans: state.mealPlans.filter(p => p.id !== (message.payload.mealPlanId || message.payload.id)),
        }));
        break;
    }
  },
}));
